/**
 * The warmth ramp — the brand kit's signature.
 *
 * "Every round of Hidein is a journey from cold to hot. That journey is the
 * palette." The kit's own ramp is calibrated for a street-scale game (4.8 km is
 * ice cold, 80 m is burning), so the stops are re-pinned to the bands the
 * engine scores on (shared/warmth.ts).
 *
 * The colour is now the *whole* readout mid-round, not a decoration on a
 * distance: cheap plentiful guesses meant exact kilometres had to go, because
 * three of them solve the round by trilateration. So the ramp does the work the
 * number used to, which is what the kit asked of it all along.
 */

import { COLDEST_BAND, warmthBand, warmthLabel } from "../../shared/warmth";

export const BRAND = {
  flare: "#FF5A36",
  ember: "#FF8A2B",
  beacon: "#FFC02E",
  sage: "#7ED4C0",
  chill: "#35C6E8",
  moss: "#12B886",
  dusk: "#1E1338",
  haze: "#F3F1FF",
  fog: "#7A6E9C",
} as const;

export type Warmth = {
  label: string;
  color: string;
  /** 0..1 along the ramp, for bars and gradients. 1 is on top of it. */
  t: number;
};

/** Indexed by band: found, boiling, burning, hot, warm, cool, ice cold. */
const BAND_COLORS = [
  BRAND.moss,
  BRAND.flare,
  BRAND.flare,
  BRAND.ember,
  BRAND.beacon,
  BRAND.sage,
  BRAND.chill,
];

export function warmthOfBand(band: number): Warmth {
  const index = Math.min(COLDEST_BAND, Math.max(0, band));
  return {
    label: warmthLabel(index),
    color: BAND_COLORS[index],
    t: 1 - index / COLDEST_BAND,
  };
}

/**
 * For the reveal, where exact distances are finally safe, and for the demo on
 * the landing page. Mid-round the client never holds a distance to pass in.
 */
export function warmthFor(distanceKm: number, winRadiusKm = 25): Warmth {
  return warmthOfBand(warmthBand(distanceKm, winRadiusKm));
}

/**
 * Voice, per the kit: tease, don't taunt. No exclamation marks — "the colour is
 * already shouting" — and never gloat when someone loses.
 */
const BAND_LINES = [
  "Found it.",
  "Boiling. You are practically standing on it.",
  "Burning. Same handful of streets.",
  "Hot. Same corner of the map.",
  "Warm. Right region, wrong spot.",
  "Cool. You have the general area, maybe.",
  "Ice cold. Nowhere near.",
];

export function warmthLine(band: number): string {
  return BAND_LINES[Math.min(COLDEST_BAND, Math.max(0, band))];
}

/** The chill-to-flare ramp as a CSS gradient. Proximity only. */
export const RAMP_CSS = `linear-gradient(90deg, ${BRAND.chill} 0%, ${BRAND.sage} 26%, ${BRAND.beacon} 58%, ${BRAND.ember} 80%, ${BRAND.flare} 100%)`;
